import { render } from '@react-email/components'
import { Link, type LoaderFunctionArgs, useLoaderData } from 'react-router'
import {
	emailPreviews,
	reactPdfPreviews,
} from '#app/utils/dev-preview-data.server'

/**
 * Consolidated dev-only preview of every email template and Playwright PDF
 * report, driven by `?type=email:<Name>` / `?type=pdf:<key>`. Replaces the
 * old email-preview{,1,2,3} routes and the stray pdf-data page.
 */

export async function loader({ request }: LoaderFunctionArgs) {
	if (process.env.NODE_ENV === 'production') {
		throw new Response('Not Found', { status: 404 })
	}

	const url = new URL(request.url)
	const type = url.searchParams.get('type') ?? `email:${emailPreviews[0].key}`

	const catalog = {
		emails: emailPreviews.map((e) => ({
			key: `email:${e.key}`,
			label: e.label,
		})),
		reactPdfs: reactPdfPreviews.map((p) => ({
			key: `pdf:${p.key}`,
			label: p.label,
		})),
	}

	if (type.startsWith('pdf:')) {
		const key = type.slice('pdf:'.length)
		if (!reactPdfPreviews.some((p) => p.key === key)) {
			throw new Response(`Unknown PDF preview: ${key}`, { status: 404 })
		}
		// react-pdf previews render as a real PDF in an <iframe> (the resource
		// route streams the bytes); there is no HTML to show.
		return {
			kind: 'reactpdf' as const,
			type,
			src: `/dev-preview/pdf?type=${encodeURIComponent(type)}`,
			html: '',
			catalog,
		}
	}

	const key = type.startsWith('email:') ? type.slice('email:'.length) : type
	const entry = emailPreviews.find((e) => e.key === key)
	if (!entry) {
		throw new Response(`Unknown email preview: ${key}`, { status: 404 })
	}
	const html = await render(entry.element())
	return {
		kind: 'email' as const,
		type: `email:${key}`,
		html,
		src: '',
		catalog,
	}
}

export default function DevPreview() {
	const { kind, type, html, src, catalog } = useLoaderData<typeof loader>()

	return (
		<div
			className="flex h-screen bg-white text-[#32302F]"
			style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}
		>
			<aside className="flex w-[260px] shrink-0 flex-col gap-6 overflow-y-auto border-r border-gray-200 px-4 py-5">
				<div className="flex items-center gap-2">
					<h1 className="text-sm font-semibold">Dev Preview</h1>
					<span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">
						Dev only
					</span>
				</div>
				<SidebarGroup title="Emails" items={catalog.emails} active={type} />
				<SidebarGroup
					title="PDF Reports (react-pdf)"
					items={catalog.reactPdfs}
					active={type}
				/>
			</aside>

			<main className="flex min-w-0 flex-1 flex-col">
				{kind === 'reactpdf' ? (
					<>
						<div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-4 py-2">
							<span className="text-xs text-gray-500">
								Live react-pdf output (no Chromium) — fully-populated demo data
							</span>
							<a
								href={src}
								target="_blank"
								rel="noreferrer"
								className="rounded-md bg-[#32302F] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#4a4745]"
							>
								Open PDF
							</a>
						</div>
						<iframe
							title="Preview"
							src={src}
							className="h-full w-full flex-1 border-0 bg-white"
						/>
					</>
				) : (
					<iframe
						title="Preview"
						srcDoc={html}
						className="h-full w-full flex-1 border-0 bg-white"
					/>
				)}
			</main>
		</div>
	)
}

function SidebarGroup({
	title,
	items,
	active,
}: {
	title: string
	items: Array<{ key: string; label: string }>
	active: string
}) {
	return (
		<div>
			<h2 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
				{title}
			</h2>
			<nav className="flex flex-col gap-0.5">
				{items.map((item) => (
					<Link
						key={item.key}
						to={`?type=${item.key}`}
						className={`rounded-md px-2 py-1.5 text-[13px] leading-snug ${
							item.key === active
								? 'bg-gray-100 font-medium text-[#32302F]'
								: 'text-gray-500 hover:bg-gray-50 hover:text-[#32302F]'
						}`}
					>
						{item.label}
					</Link>
				))}
			</nav>
		</div>
	)
}
